import { Injectable, inject, signal, computed, Signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, take, tap } from 'rxjs';
import { RelatedToType } from '@shared/enums';
import { Activity, ActivityResponse } from '@core/models/account.types';

export interface ActivityParams {
  page?: number;
  per_page?: number;
  project_id?: number;
  field?: string;
  related_to_id?: number;
  related_to_type?: RelatedToType | string;
  require_summary?: boolean;
  [key: string]: string | number | boolean | undefined;
}

export interface ActivityPagination {
  activePage: number;
  pageSize: number;
  totalPages: number;
}

export interface ActivityState {
  activities: Activity[];
  total: number;
  isLoading: boolean;
  error: string | null;
  pagination: ActivityPagination;
}

@Injectable({
  providedIn: 'root',
})
export class ActivityService {
  private readonly httpClient = inject(HttpClient);

  // Private signals for internal state management
  private readonly _activities = signal<Activity[]>([]);
  private readonly _total = signal<number>(0);
  private readonly _isLoading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);
  private readonly _pagination = signal<ActivityPagination>({
    activePage: 1,
    pageSize: 20,
    totalPages: 0,
  });

  // Computed signal for the full state
  public readonly state = computed<ActivityState>(() => ({
    activities: this._activities(),
    total: this._total(),
    isLoading: this._isLoading(),
    error: this._error(),
    pagination: this._pagination(),
  }));

  // Computed signal for activity count
  public readonly activitiesCount = computed(() => this._total());

  // Computed signal for the existence of activities
  public readonly hasActivities: Signal<boolean> = computed(() => this._activities().length > 0);

  // Public readonly signals for components to consume
  public readonly pagination = this._pagination.asReadonly();

  /**
   * Fetch activities with the given parameters
   * @param params - Activity parameters for filtering and pagination
   * @returns Observable of ActivityResponse
   */
  getActivities(params: ActivityParams): Observable<ActivityResponse> {
    return this.httpClient.get<ActivityResponse>('activities', { params }).pipe(take(1));
  }

  /**
   * Fetch activities and update internal state
   * @param params - Activity parameters for filtering and pagination
   * @returns Observable of ActivityResponse
   */
  fetchActivities(params: ActivityParams): Observable<ActivityResponse> {
    this._isLoading.set(true);
    this._error.set(null);

    return this.httpClient.get<ActivityResponse>('activities', { params }).pipe(
      take(1),
      tap({
        next: (response: ActivityResponse) => {
          this._activities.set(response.activities);
          this._total.set(response.total);
          // Update pagination with current params and total
          this._pagination.set({
            activePage: params.page || 1,
            pageSize: params.per_page || 20,
            totalPages: response.total,
          });
        },
        error: (error: any) => {
          this._error.set(error?.message || 'Failed to fetch activities');
        },
        complete: () => {
          this._isLoading.set(false);
        },
      }),
    );
  }

  /**
   * Refresh activities with current parameters
   * This method can be called by parent components to trigger a refresh
   * @param params - Activity parameters for filtering and pagination
   */
  refreshActivities(params: ActivityParams): void {
    this.fetchActivities(params).subscribe();
  }

  /**
   * Clear all activity data and reset state
   */
  clearActivities(): void {
    this._activities.set([]);
    this._total.set(0);
    this._isLoading.set(false);
    this._error.set(null);
    this._pagination.set({
      activePage: 1,
      pageSize: 20,
      totalPages: 0,
    });
  }

  /**
   * Update pagination and fetch activities
   * @param page - Page number
   * @param pageSize - Items per page
   * @param currentParams - Current activity parameters
   */
  updatePagination(page: number, pageSize: number, currentParams: ActivityParams): void {
    const newParams: ActivityParams = {
      ...currentParams,
      page,
      per_page: pageSize,
    };
    this.fetchActivities(newParams).subscribe();
  }

  /**
   * Set pagination without fetching (for UI updates)
   * @param pagination - New pagination state
   */
  setPagination(pagination: ActivityPagination): void {
    this._pagination.set(pagination);
  }

  /**
   * Get activities for a specific entity
   * @param relatedToId - ID of the related entity
   * @param relatedToType - Type of the related entity
   * @param page - Page number (default: 1)
   * @param perPage - Items per page (default: 20)
   * @returns Observable of ActivityResponse
   */
  getActivitiesForEntity(
    relatedToId: number,
    relatedToType: RelatedToType,
    page: number = 1,
    perPage: number = 20,
  ): Observable<ActivityResponse> {
    const params: ActivityParams = {
      page,
      per_page: perPage,
      related_to_id: relatedToId,
      related_to_type: relatedToType,
    };

    return this.fetchActivities(params);
  }

  /**
   * Get activities for a specific project
   * @param projectId - Project ID
   * @param page - Page number (default: 1)
   * @param perPage - Items per page (default: 20)
   * @returns Observable of ActivityResponse
   */
  getActivitiesForProject(projectId: number, page: number = 1, perPage: number = 20): Observable<ActivityResponse> {
    const params: ActivityParams = {
      page,
      per_page: perPage,
      project_id: projectId,
    };

    return this.fetchActivities(params);
  }

  /**
   * Get activities for a specific field
   * @param projectId - Project ID
   * @param field - Field name
   * @param page - Page number (default: 1)
   * @param perPage - Items per page (default: 20)
   * @returns Observable of ActivityResponse
   */
  getActivitiesForField(
    projectId: number,
    field: string,
    page: number = 1,
    perPage: number = 20,
  ): Observable<ActivityResponse> {
    const params: ActivityParams = {
      page,
      per_page: perPage,
      project_id: projectId,
      field,
    };

    return this.fetchActivities(params);
  }
}
